import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../services/api';
import { UserResponse } from '../../types';
import toast from 'react-hot-toast';
import { Search, X, ChevronLeft, ChevronRight, Shield, UserCheck, UserX, Trash2 } from 'lucide-react';

const AdminUsersTab: React.FC = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [newRole, setNewRole] = useState('');

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminService.listUsers(page, size, search || undefined);
      setUsers(response.data.data.users);
      setTotalPages(response.data.data.totalPages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [page, size, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAssignRole = async (userId: string) => {
    if (!newRole) return;
    try {
      await adminService.assignRole(userId, newRole);
      toast.success('Role assigned successfully!');
      fetchUsers();
      setSelectedUser(null);
      setNewRole('');
    } catch {
      toast.error('Failed to assign role');
    }
  };

  const handleRemoveRole = async (userId: string, roleName: string) => {
    try {
      await adminService.removeRole(userId, roleName);
      toast.success('Role removed successfully!');
      fetchUsers();
    } catch {
      toast.error('Failed to remove role');
    }
  };

  const handleToggleStatus = async (user: UserResponse) => {
    const enabling = !user.enabled;
    if (!enabling && !window.confirm(`Deactivate user "${user.username}"? They will no longer be able to log in.`)) {
      return;
    }
    try {
      await adminService.setUserStatus(user.id, enabling);
      toast.success(enabling ? 'User activated' : 'User deactivated');
      fetchUsers();
    } catch {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (user: UserResponse) => {
    if (!window.confirm(`Permanently delete user "${user.username}"? This cannot be undone.`)) {
      return;
    }
    try {
      await adminService.deleteUser(user.id);
      toast.success('User deleted');
      if (selectedUser?.id === user.id) setSelectedUser(null);
      fetchUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  return (
    <>
      <div className="bg-white p-4 rounded-card mb-6 shadow-soft">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            placeholder="Search by username or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 h-11 px-3 text-sm border border-zinc-200 rounded-btn transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
          />
          <button type="submit" className="h-11 px-4 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2">
            <Search className="w-4 h-4" />
            Search
          </button>
        </form>
      </div>

      {isLoading ? (
        <div className="text-center p-12 text-secondary-foreground">Loading users...</div>
      ) : (
        <>
          <div className="bg-white rounded-card shadow-soft">
            <div className="overflow-x-auto">
            <table className="w-full border-collapse [&_tr:last-child_td]:border-b-0">
              <thead>
                <tr>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Username</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Email</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Name</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Roles</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Status</th>
                  <th className="p-4 text-left border-b border-zinc-100 bg-subtle font-semibold text-foreground text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-subtle">
                    <td className="p-4 text-left border-b border-zinc-100">{user.username}</td>
                    <td className="p-4 text-left border-b border-zinc-100">{user.email}</td>
                    <td className="p-4 text-left border-b border-zinc-100">{user.firstName} {user.lastName}</td>
                    <td className="p-4 text-left border-b border-zinc-100">
                      <div className="flex flex-wrap gap-1">
                        {user.roles?.map((role) => (
                          <span key={role} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-full text-xs text-foreground font-medium">
                            {role}
                            {role !== 'USER' && (
                              <button
                                onClick={() => handleRemoveRole(user.id, role)}
                                title="Remove role"
                                className="bg-none border-none cursor-pointer text-sm text-secondary-foreground p-0 leading-none hover:text-red-500 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-4 text-left border-b border-zinc-100">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                        {user.enabled ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-left border-b border-zinc-100">
                      <div className="flex items-center gap-2">
                        <button
                          className="h-8 px-3 text-xs font-medium text-brand-500 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors inline-flex items-center gap-1.5"
                          onClick={() => setSelectedUser(user)}
                        >
                          <Shield className="w-3.5 h-3.5" />
                          Manage Roles
                        </button>
                        <button
                          className={`h-8 px-3 text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1.5 ${user.enabled ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}
                          onClick={() => handleToggleStatus(user)}
                        >
                          {user.enabled ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                          {user.enabled ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="h-8 px-3 text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 rounded-btn transition-colors inline-flex items-center gap-1.5"
                          onClick={() => handleDeleteUser(user)}
                          title="Delete user"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                className="h-9 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground transition-colors hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-sm text-secondary-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
                className="h-9 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground transition-colors hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-card p-8 w-full max-w-[500px] shadow-elevated" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-foreground mb-6 font-display">Manage Roles for {selectedUser.username}</h2>
            <div className="mb-6">
              <h3 className="text-sm text-secondary-foreground mb-3 font-medium">Current Roles:</h3>
              <div className="flex flex-wrap gap-1">
                {selectedUser.roles?.map((role) => (
                  <span key={role} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-full text-xs text-foreground font-medium">
                    {role}
                  </span>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-sm text-secondary-foreground mb-3 font-medium">Add Role:</h3>
              <div className="flex gap-2">
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="flex-1 h-11 px-3 text-sm border border-zinc-200 rounded-btn text-foreground bg-white"
                >
                  <option value="">Select a role</option>
                  <option value="ADMIN">Admin</option>
                  <option value="USER">User</option>
                </select>
                <button
                  className="h-10 px-5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200"
                  onClick={() => handleAssignRole(selectedUser.id)}
                  disabled={!newRole}
                >
                  Add Role
                </button>
              </div>
            </div>
            <button
              className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-foreground text-sm font-medium rounded-btn transition-colors"
              onClick={() => setSelectedUser(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUsersTab;
